import { useMemo } from "react"
import {
	IconCheckOutline18 as Check,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { CommandItem } from "@productlane/ui/src/components/internal/command"

import type { SharedFilterItemsProps } from "./types"
import { api } from "@/lib/api"
import { useZeroWorkspace } from "@/lib/zero"
import { Owner } from "@/screens/app/threads/components/right-bar/customer-properties/icons/owner"
import { useThreadsFilterState } from "../threads-filter-state"

export function CompanyOwnerFilterItems({ onSelect }: SharedFilterItemsProps) {
	const workspace = useZeroWorkspace()
	const teamId = workspace?.defaultLinearTeamId ?? ""
	const membersQuery = api.linear.members.useQuery(
		{ teamId },
		{ enabled: !!teamId, cacheTime: Infinity, staleTime: Infinity },
	)
	const members = useMemo(
		() =>
			[...(membersQuery.data ?? [])].sort((a, b) =>
				a.displayName.localeCompare(b.displayName),
			),
		[membersQuery.data],
	)

	const { companyOwnerIds, addCompanyOwner, removeCompanyOwner } =
		useThreadsFilterState()

	return (
		<>
			<CommandItem
				Icon={UserX}
				onSelect={() => {
					if (companyOwnerIds.includes(null)) {
						removeCompanyOwner(null)
					} else {
						addCompanyOwner(null)
					}
					onSelect()
				}}
			>
				<span className="flex w-full items-center justify-between">
					<span className="truncate">No owner</span>
					<Check
						className={cn("h-4 w-4 shrink-0", {
							"opacity-100": companyOwnerIds.includes(null),
							"opacity-0": !companyOwnerIds.includes(null),
						})}
					/>
				</span>
			</CommandItem>
			<hr className="my-2" />
			{members.map((member) => (
				<CommandItem
					key={member.id}
					onSelect={() => {
						if (companyOwnerIds.includes(member.id)) {
							removeCompanyOwner(member.id)
						} else {
							addCompanyOwner(member.id)
						}
						onSelect()
					}}
				>
					<span className="flex w-full items-center gap-2">
						{member.avatarUrl ? (
							<img
								src={member.avatarUrl}
								alt={member.displayName}
								className="h-4 w-4 shrink-0 rounded-full object-cover"
							/>
						) : (
							<Owner className="h-4 w-4 shrink-0 text-secondary" />
						)}
						<span className="flex-1 truncate">{member.displayName}</span>
						<Check
							className={cn("h-4 w-4 shrink-0", {
								"opacity-100": companyOwnerIds.includes(member.id),
								"opacity-0": !companyOwnerIds.includes(member.id),
							})}
						/>
					</span>
				</CommandItem>
			))}
		</>
	)
}
